import { FormEvent, ReactNode, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, CardHeader, Checkbox, ErrorState, Input, MoneyInput, PageHeader, PageLoader, Select, Switch, Textarea, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { serverFieldErrors } from '../../../utils/validation';
import { CustomerSummary } from '../components/CustomerSummary/CustomerSummary';
import { useCustomer } from '../hooks/useCustomers';
import {
  CUSTOMER_SERVER_FIELDS, CUSTOMER_TYPES, CustomerFormValues, EMPTY_CUSTOMER, fromCustomer, toCustomerInput, validateCustomer,
} from '../schemas/customerSchema';
import { customerService } from '../services/customerService';
import type { FormErrors } from '../../../hooks/useForm';

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader title={title} subtitle={subtitle} className="border-b border-line pb-4" />
      <div className="grid gap-4 p-5 sm:grid-cols-2">{children}</div>
    </Card>
  );
}

export default function CustomerForm() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const toast = useToast();
  const [form, setForm] = useState<CustomerFormValues>(EMPTY_CUSTOMER);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);

  const { data: customer, loading, error, reload } = useCustomer(id);
  useCrumb(`/customers/${id}`, customer?.name);

  useEffect(() => {
    if (editing && customer) setForm(fromCustomer(customer));
  }, [editing, customer]);

  const set = (key: keyof CustomerFormValues) => (valueOrEvent: any) => {
    const v = valueOrEvent?.target ? valueOrEvent.target.value : valueOrEvent;
    setForm((f) => ({ ...f, [key]: v }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const back = () => navigate(editing ? `/customers/${id}` : '/customers');

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    const e = validateCustomer(form);
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error('Please fix the highlighted fields');
      return;
    }

    const payload = toCustomerInput(form, editing);

    setSaving(true);
    try {
      const saved = id ? await customerService.update(id, payload) : await customerService.create(payload);
      toast.success(editing ? 'Customer updated' : 'Customer added');
      navigate(`/customers/${saved._id}`, { replace: true });
    } catch (err) {
      setErrors(serverFieldErrors(err, CUSTOMER_SERVER_FIELDS));
      toast.error(err.message);
      setSaving(false);
    }
  };

  if (error) return <Card><ErrorState error={error} onRetry={reload} /></Card>;
  if (editing && loading && !customer) return <PageLoader />;

  const actions = (
    <>
      <Button variant="outline" onClick={back} disabled={saving}>Cancel</Button>
      <Button type="submit" form="customer-form" leftIcon="check" loading={saving}>
        {editing ? 'Save changes' : 'Save customer'}
      </Button>
    </>
  );

  return (
    <>
      <PageHeader
        title={editing ? `Edit ${customer?.name ?? 'customer'}` : 'Add customer'}
        subtitle={editing ? customer?.customerCode : 'Save a customer once and pick them on quotations, jobs and invoices.'}
        actions={actions}
      />

      <form id="customer-form" onSubmit={submit} noValidate className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-5">
          <Section title="Basic details" subtitle="Who they are and how to reach them">
            <Input label="Name" required autoFocus value={form.name} onChange={set('name')} error={errors.name} placeholder="Full name or company" containerClassName="sm:col-span-2" />
            <Input label="Mobile" type="tel" inputMode="numeric" leftIcon="phone" value={form.phone} onChange={set('phone')} error={errors.phone} placeholder="10 digits" />
            <Input label="Other phone" type="tel" inputMode="numeric" value={form.altPhone} onChange={set('altPhone')} placeholder="Optional" />
            <Select label="Customer type" options={CUSTOMER_TYPES} value={form.customerType} onChange={set('customerType')} />
            <Input label="Email" type="email" value={form.email} onChange={set('email')} error={errors.email} placeholder="Optional" />
            {form.customerType === 'business' && (
              <Input
                label="GSTIN"
                value={form.gstin}
                onChange={(e) => set('gstin')(e.target.value.toUpperCase())}
                error={errors.gstin}
                maxLength={15}
                hint="Needed if they want GST credit on your bill"
                containerClassName="sm:col-span-2"
              />
            )}
          </Section>

          <Section title="Address" subtitle="Used on quotations and invoices (Maharashtra)">
            <Textarea label="House / street" rows={2} value={form.line} onChange={set('line')} error={errors.line} containerClassName="sm:col-span-2" placeholder="Flat no, building, road, area" />
            <Input label="City" value={form.city} onChange={set('city')} error={errors.city} />
            <Input label="Pincode" inputMode="numeric" maxLength={6} value={form.pincode} onChange={set('pincode')} error={errors.pincode} />
          </Section>

          <Section title="Old balance and notes" subtitle="Only if they owed you, or paid in advance, before this app">
            <MoneyInput
              label="Amount"
              value={form.openingBalance}
              onChange={set('openingBalance')}
              placeholder="0"
              hint={form.isAdvance ? 'Money the customer already paid you' : 'Money the customer owed you before using this app'}
            />
            <div className="flex items-start pt-8">
              <Checkbox label="This is an advance" checked={form.isAdvance} onChange={set('isAdvance')} />
            </div>
            <Textarea label="Notes" rows={2} value={form.notes} onChange={set('notes')} maxLength={2000} containerClassName="sm:col-span-2" placeholder="Landmark, preferred time to visit..." />
            {editing && (
              <Switch
                className="rounded-lg border border-line bg-navy-50/60 p-3 sm:col-span-2"
                label="Active customer"
                description="Inactive customers are hidden when making new quotations"
                checked={form.isActive}
                onChange={set('isActive')}
              />
            )}
          </Section>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">{actions}</div>
        </div>

        <aside className="hidden xl:sticky xl:top-24 xl:block">
          <CustomerSummary form={form} editing={editing} />
        </aside>
      </form>
    </>
  );
}
